import React, { useEffect, useMemo, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { useHospitalData } from '../../hooks/useHospitalData'
import { usePersistedState } from '../../hooks/usePersistedState'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import NoDataOverlay from '../../components/Common/NoDataOverlay'
import StackedBarChart from '../../components/Charts/StackedBarChart'
import { parseMonthToValue } from '../../utils/dataUtils'
import { MonthInfo, generateMonthList } from '../../utils/coreUtils'
import {
    createChartExportHandlers,
    createMenuToggle,
    setupMenuClickOutside,
    exportToCSV,
} from '../../utils/exportUtils'
import './HuddleBoardPreferences.css'

interface ZohoResponse {
    response?: {
        result?: {
            column_order?: string[]
            rows?: any[][]
        }
    }
}

const Q6_QUESTION =
    'Did the team ask the patient to share Preferences related to the current plan being discussed?'
const Q7_QUESTION =
    'Were any patient preferences written on the board, either during this huddle or previously?'

const Q6_ANSWER_ORDER: string[] = ['Yes', 'No']
const Q6_ANSWER_COLORS: Record<string, string> = {
    Yes: '#2C5985',
    No: '#E48E43',
}
const Q6_DATA_LABEL_COLORS: Record<string, string> = {
    Yes: '#ffffff',
    No: '#ffffff',
}

const Q7_ANSWER_ORDER: string[] = ['Yes', 'Yes, Previously', 'No']
const Q7_ANSWER_COLORS: Record<string, string> = {
    Yes: '#2C5985',
    'Yes, Previously': '#AEDCD5',
    No: '#E48E43',
}
const Q7_DATA_LABEL_COLORS: Record<string, string> = {
    Yes: '#ffffff',
    'Yes, Previously': '#626262',
    No: '#ffffff',
}

const SHARED_LEGEND_ORDER: string[] = ['No', 'Yes, Previously', 'Yes']
const SHARED_LEGEND_COLORS: Record<string, string> = {
    No: '#E48E43',
    'Yes, Previously': '#AEDCD5',
    Yes: '#2C5985',
}

const SECTION_MENU_ID = 'hdl-board-preferences-section-export-menu'
const HDL_SLIDER_KEY = 'hdl_slider'

const getSliderIndices = (monthList: MonthInfo[], range: [number, number]): [number, number] => {
    if (!monthList.length) return [0, 0]
    const maxIndex = monthList.length - 1
    const [startVal, endVal] = range
    if (!startVal || !endVal) return [0, maxIndex]

    let startIdx = monthList.findIndex(m => m.value === startVal)
    if (startIdx === -1) startIdx = monthList.findIndex(m => m.value >= startVal)
    if (startIdx === -1) startIdx = 0

    let endIdx = -1
    for (let i = maxIndex; i >= 0; i--) {
        if (monthList[i].value <= endVal) { endIdx = i; break }
    }
    if (endIdx === -1) endIdx = maxIndex

    startIdx = Math.max(0, Math.min(startIdx, maxIndex))
    endIdx = Math.max(0, Math.min(endIdx, maxIndex))
    if (startIdx > endIdx) return [0, maxIndex]
    return [startIdx, endIdx]
}

const normalizeAnswer = (raw: string): string | null => {
    const trimmed = raw.trim()
    if (!trimmed) return null
    const lower = trimmed.toLowerCase()
    if (lower === 'yes') return 'Yes'
    if (lower === 'no') return 'No'
    if (lower === 'yes, previously' || lower === 'yes previously') return 'Yes, Previously'
    return null
}

const HuddleBoardPreferences: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()

    const hospitalIds = useMemo(
        () => (Array.isArray(selectedHospitalIds) ? selectedHospitalIds : []),
        [selectedHospitalIds]
    )

    const { data: q6Data, loading: q6Loading, initialLoading: q6InitialLoading, error: q6Error } = useHospitalData<ZohoResponse>(
        '/api/hdl/board_preferences_q6',
        hospitalIds
    )
    const { data: q7Data, loading: q7Loading, initialLoading: q7InitialLoading, error: q7Error } = useHospitalData<ZohoResponse>(
        '/api/hdl/board_preferences_q7',
        hospitalIds
    )

    const loading = q6Loading || q7Loading
    const initialLoading = q6InitialLoading || q7InitialLoading
    const error = q6Error || q7Error

    const sectionRef = useRef<HTMLDivElement>(null)
    const sectionExports = useMemo(
        () => createChartExportHandlers(
            sectionRef,
            'Discussion & Board Use: Preferences — Q6 & Q7',
            'huddle_board_preferences_q6_q7',
            { fontBoost: true, exportTitle: 'Discussion & Board Use: Preferences', exportTitleAlign: 'center', exportTitleFontSize: '28px', extraHideSelectors: ['.stacked-bar-chart-legend'] }
        ),
        []
    )
    const toggleSectionMenu = useMemo(() => createMenuToggle(SECTION_MENU_ID), [])

    useEffect(() => {
        return setupMenuClickOutside(() => sectionExports.closeMenu(SECTION_MENU_ID))
    }, [sectionExports])

    const q6Rows: any[][] = q6Data?.response?.result?.rows || []
    const q7Rows: any[][] = q7Data?.response?.result?.rows || []

    const monthList: MonthInfo[] = useMemo(() => {
        const combined = [...q6Rows, ...q7Rows]
        if (!combined.length) return []
        return generateMonthList(combined, 2)
    }, [q6Rows, q7Rows])

    const hospitalIdsKey = hospitalIds.join('_')
    const persistenceKey = `${HDL_SLIDER_KEY}_${hospitalIdsKey}`
    const [sliderRangeValues, setSliderRangeValues] = usePersistedState<[number, number]>(
        persistenceKey,
        [0, 0]
    )

    const userInteractedRef = useRef(false)
    const initSeenRef = useRef(false)
    const lastDetectedKeyRef = useRef<string | null>(null)

    if (hospitalIds.length > 0 && lastDetectedKeyRef.current !== persistenceKey) {
        const isFirstInit = !initSeenRef.current
        lastDetectedKeyRef.current = persistenceKey
        initSeenRef.current = true

        if (isFirstInit) {
            try {
                const item = sessionStorage.getItem(persistenceKey)
                const parsed = item !== null ? JSON.parse(item) : null
                userInteractedRef.current =
                    Array.isArray(parsed) && parsed[0] !== 0 && parsed[1] !== 0
            } catch {
                userInteractedRef.current = false
            }
        } else {
            userInteractedRef.current = false
        }
    }

    useEffect(() => {
        if (!monthList.length) return
        if (loading) return
        const maxIdx = monthList.length - 1
        const fullRange: [number, number] = [monthList[0].value, monthList[maxIdx].value]
        const [curStart, curEnd] = sliderRangeValues

        if (!userInteractedRef.current) {
            if (curStart !== fullRange[0] || curEnd !== fullRange[1]) {
                setSliderRangeValues(fullRange)
            }
            return
        }

        const stillValid =
            curStart >= monthList[0].value &&
            curEnd <= monthList[maxIdx].value &&
            curStart <= curEnd
        if (!curStart || !curEnd || !stillValid) {
            userInteractedRef.current = false
            setSliderRangeValues(fullRange)
        }
    }, [hospitalIdsKey, monthList, loading, sliderRangeValues, setSliderRangeValues])

    const [startIdx, endIdx] = useMemo(
        () => getSliderIndices(monthList, sliderRangeValues),
        [monthList, sliderRangeValues]
    )

    type HospitalAggregate = {
        id: string
        name: string
        counts: Record<string, number>
        total: number
        nCount: number
        percentages: Record<string, number>
    }

    type Aggregated = {
        counts: Record<string, number>
        total: number
        nCount: number
        percentages: Record<string, number>
        hospitalCount: number
        perHospital: HospitalAggregate[]
    }

    const aggregateRows = (sourceRows: any[][], answerOrder: string[]): Aggregated => {
        const empty: Aggregated = { counts: {}, total: 0, nCount: 0, percentages: {}, hospitalCount: 0, perHospital: [] }
        if (!monthList.length || !sourceRows.length) return empty

        const minVal = monthList[startIdx]?.value ?? 0
        const maxVal = monthList[endIdx]?.value ?? 0

        const perHospital: Record<string, {
            id: string; name: string;
            counts: Record<string, number>; total: number; nCount: number;
        }> = {}
        const seenGroups = new Set<string>()

        sourceRows.forEach(r => {
            const hospId = String(r[0])
            const hospName = String(r[1] || '').trim()
            const monthVal = parseMonthToValue(r[2])
            if (!monthVal || monthVal < minVal || monthVal > maxVal) return
            const answer = normalizeAnswer(String(r[3] || ''))
            if (!answer) return
            const numerator = Number(r[4]) || 0
            const denom = Number(r[5]) || 0
            const nCountVal = Number(r[7]) || 0

            if (!perHospital[hospId]) perHospital[hospId] = { id: hospId, name: hospName, counts: {}, total: 0, nCount: 0 }
            const h = perHospital[hospId]
            if (!h.name && hospName) h.name = hospName
            h.counts[answer] = (h.counts[answer] || 0) + numerator

            const groupKey = `${hospId}|${monthVal}`
            if (!seenGroups.has(groupKey)) {
                seenGroups.add(groupKey)
                h.total += denom
                if (denom > 0) h.nCount += nCountVal
            }
        })

        const contributing = Object.values(perHospital).filter(h => h.total > 0)
        if (contributing.length === 0) {
            return { counts: {}, total: 0, nCount: 0, percentages: {}, hospitalCount: 0, perHospital: [] }
        }

        const counts: Record<string, number> = {}
        let total = 0
        let nCount = 0
        for (const h of contributing) {
            for (const [a, c] of Object.entries(h.counts)) counts[a] = (counts[a] || 0) + c
            total += h.total
            nCount += h.nCount
        }

        const percentages: Record<string, number> = {}
        for (const a of answerOrder) {
            percentages[a] = total > 0 ? ((counts[a] || 0) / total) * 100 : 0
        }

        const perHospitalSorted: HospitalAggregate[] = contributing
            .slice()
            .sort((a, b) => a.id.localeCompare(b.id))
            .map(h => {
                const pct: Record<string, number> = {}
                for (const a of answerOrder) {
                    pct[a] = h.total > 0 ? ((h.counts[a] || 0) / h.total) * 100 : 0
                }
                return { id: h.id, name: h.name, counts: { ...h.counts }, total: h.total, nCount: h.nCount, percentages: pct }
            })

        return { counts, total, nCount, percentages, hospitalCount: contributing.length, perHospital: perHospitalSorted }
    }

    const q6Aggregated = useMemo(
        () => aggregateRows(q6Rows, Q6_ANSWER_ORDER),
        [q6Rows, monthList, startIdx, endIdx, isMultiSelect]
    )
    const q7Aggregated = useMemo(
        () => aggregateRows(q7Rows, Q7_ANSWER_ORDER),
        [q7Rows, monthList, startIdx, endIdx, isMultiSelect]
    )

    const buildChartData = (
        agg: Aggregated,
        answerOrder: string[],
        colorMap: Record<string, string>,
        labelColorMap: Record<string, string>
    ) => {
        // Only plot when the API actually returned responses (numerators > 0).
        // Denominator > 0 with all-zero numerators means no answers were recorded → no data.
        const responded = answerOrder.reduce((s, a) => s + (agg.counts[a] || 0), 0)
        if (responded <= 0) {
            return { series: [], colors: [], dataLabelColors: [], hasData: false }
        }
        // Plot the API's actual numerator/denominator percentages (plain round).
        // Do NOT pad to 100% — if some huddles didn't answer, the bar legitimately
        // falls short. Padding (largest-remainder) fabricated values not in the API.
        const adjMap: Record<string, number> = {}
        answerOrder.forEach(a => { adjMap[a] = Math.round(agg.percentages[a] || 0) })
        const visible = answerOrder.filter(a => adjMap[a] > 0)
        return {
            series: visible.map(a => ({ name: a, data: [adjMap[a]] })),
            colors: visible.map(a => colorMap[a]),
            dataLabelColors: visible.map(a => labelColorMap[a]),
            hasData: true,
        }
    }

    const q6Chart = useMemo(
        () => buildChartData(q6Aggregated, Q6_ANSWER_ORDER, Q6_ANSWER_COLORS, Q6_DATA_LABEL_COLORS),
        [q6Aggregated]
    )
    const q7Chart = useMemo(
        () => buildChartData(q7Aggregated, Q7_ANSWER_ORDER, Q7_ANSWER_COLORS, Q7_DATA_LABEL_COLORS),
        [q7Aggregated]
    )

    const q6HasData = q6Chart.hasData
    const q7HasData = q7Chart.hasData

    // Encounters (n_count) are independent of answer distribution: a question may have
    // observed huddles (N) but no Yes/No answers recorded (numerators all 0).
    const q6HasEncounters = q6Aggregated.nCount > 0
    const q7HasEncounters = q7Aggregated.nCount > 0

    const handleDownloadCSV = () => {
        const rowsOut: (string | number)[][] = []
        const allHospitalsStr = hospitalIds.join(',')
        const startLabel = monthList[startIdx]?.label || ''
        const endLabel = monthList[endIdx]?.label || ''
        const monthRange = startLabel && endLabel ? `${startLabel} - ${endLabel}` : ''

        const pushBlock = (
            hospitalId: string,
            question: string,
            counts: Record<string, number>,
            percentages: Record<string, number>,
            total: number,
            nCount: number,
            answerOrder: string[]
        ) => {
            answerOrder.forEach(answer => {
                const count = counts[answer] || 0
                const percent = total > 0 ? `${Math.round(percentages[answer] || 0)}%` : ''
                rowsOut.push([hospitalId, monthRange, question, answer, count, percent])
            })
            rowsOut.push([hospitalId, monthRange, question, 'Total (N)', nCount || 0, ''])
        }

        if (q6Aggregated.perHospital.length > 0) {
            pushBlock(allHospitalsStr, Q6_QUESTION, q6Aggregated.counts, q6Aggregated.percentages, q6Aggregated.total, q6Aggregated.nCount, Q6_ANSWER_ORDER)
        }

        if (q7Aggregated.perHospital.length > 0) {
            pushBlock(allHospitalsStr, Q7_QUESTION, q7Aggregated.counts, q7Aggregated.percentages, q7Aggregated.total, q7Aggregated.nCount, Q7_ANSWER_ORDER)
        }

        exportToCSV({
            headers: ['Hospital ID(s)', 'Month Range', 'Question', 'Answer', 'Count', 'Percentage'],
            rows: rowsOut,
            filename: 'huddle_board_preferences_q6_q7',
        })
    }

    const showInitialLoading = initialLoading && loading
    const isRefreshing = !initialLoading && loading
    const hasHospitals = hospitalIds.length > 0
    const noData = !initialLoading && !loading && !monthList.length

    return (
        <div className="hdl-board-preferences">
            <div className="hdl-page-header">
                <h1 className="hdl-page-title">Discussion & Board Use: Preferences</h1>
                <div className="hdl-slider-wrap">
                    {monthList.length > 0 && (
                        <DateRangeSlider
                            monthList={monthList}
                            startIndex={startIdx}
                            endIndex={endIdx}
                            onChange={(s, e) => {
                                const sv = monthList[s]?.value || 0
                                const ev = monthList[e]?.value || 0
                                if (sv && ev) {
                                    userInteractedRef.current = true
                                    setSliderRangeValues([sv, ev])
                                }
                            }}
                        />
                    )}
                </div>
            </div>

            <div className={`hdl-card${hasHospitals && !error && noData ? ' hdl-card-empty' : ''}`}>
                {showInitialLoading && <LoadingNotice title="Loading preferences data…" />}

                {!hasHospitals && (
                    <div className="hdl-notice">Select at least one hospital to view data.</div>
                )}

                {hasHospitals && error && (
                    <div className="hdl-notice hdl-notice-error">Failed to load data: {error}</div>
                )}

                <NoDataOverlay visible={hasHospitals && !error && noData} />

                {hasHospitals && !error && !noData && (
                    <div className="hdl-board-preferences-section" ref={sectionRef}>
                        <div className="hdl-board-preferences-section-head-row">
                            <div className="hdl-board-preferences-section-title">Q6 &amp; Q7</div>
                            <div className="export-menu export-ignore">
                                <button className="hamburger" onClick={toggleSectionMenu}>&#9776;</button>
                                <div id={SECTION_MENU_ID} className="export-dropdown">
                                    <div onClick={() => sectionExports.handleDownloadSVG(SECTION_MENU_ID)}>Download SVG</div>
                                    <div onClick={() => sectionExports.handleDownloadPNG(SECTION_MENU_ID)}>Download PNG</div>
                                    <div onClick={() => { handleDownloadCSV(); sectionExports.closeMenu(SECTION_MENU_ID); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>

                        <div className="hdl-board-preferences-charts">
                            <div className="hdl-board-preferences-question">{Q6_QUESTION}</div>
                            <div className="hdl-board-preferences-question">{Q7_QUESTION}</div>
                            <div className="hdl-board-preferences-legend-spacer" aria-hidden="true" />

                            {q6HasData ? (
                                <div className="hdl-board-preferences-chart-stack">
                                    <StackedBarChart
                                        categories={['']}
                                        series={q6Chart.series}
                                        colors={q6Chart.colors}
                                        height={480}
                                        columnWidth="100%"
                                        fontFamily="Poppins, 'Segoe UI', sans-serif"
                                        yAxisShow={false}
                                        xAxisShow={false}
                                        showGrid={false}
                                        sparkline={true}
                                        showDataLabels={true}
                                        dataLabelColors={q6Chart.dataLabelColors}
                                        dataLabelMinPercent={8}
                                        dataLabelFontSize="14px"
                                        fillOpacity={1}
                                        exportFilename="huddle_board_preferences_q6"
                                    />
                                </div>
                            ) : (
                                <div className="hdl-board-preferences-empty">No data for the selected range</div>
                            )}

                            {q7HasData ? (
                                <div className="hdl-board-preferences-chart-stack">
                                    <StackedBarChart
                                        categories={['']}
                                        series={q7Chart.series}
                                        colors={q7Chart.colors}
                                        height={480}
                                        columnWidth="100%"
                                        fontFamily="Poppins, 'Segoe UI', sans-serif"
                                        yAxisShow={false}
                                        xAxisShow={false}
                                        showGrid={false}
                                        sparkline={true}
                                        showDataLabels={true}
                                        dataLabelColors={q7Chart.dataLabelColors}
                                        dataLabelMinPercent={8}
                                        dataLabelFontSize="14px"
                                        fillOpacity={1}
                                        exportFilename="huddle_board_preferences_q7"
                                    />
                                </div>
                            ) : (
                                <div className="hdl-board-preferences-empty">No data for the selected range</div>
                            )}

                            <div className="hdl-board-preferences-legend">
                                {SHARED_LEGEND_ORDER.map(answer => (
                                    <div key={answer} className="hdl-board-preferences-legend-item">
                                        <span
                                            className="hdl-board-preferences-legend-dot"
                                            style={{ backgroundColor: SHARED_LEGEND_COLORS[answer] }}
                                        />
                                        <span className="hdl-board-preferences-legend-label">{answer}</span>
                                    </div>
                                ))}
                            </div>

                            {(q6HasEncounters || q7HasEncounters) && (
                                <div className="hdl-board-preferences-n-row">
                                    <span className="hdl-board-preferences-n-label">N=</span>
                                    <span className="hdl-board-preferences-bar-n">{q6HasEncounters ? q6Aggregated.nCount : ''}</span>
                                    <span className="hdl-board-preferences-bar-n">{q7HasEncounters ? q7Aggregated.nCount : ''}</span>
                                    <span aria-hidden="true" />
                                </div>
                            )}
                        </div>

                        {isRefreshing && (
                            <div className="chart-loading-overlay">
                                <div>Loading...</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default HuddleBoardPreferences
